import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import PageSections from "../components/PageSections";
import { fetchPage } from "../lib/api";
import { PACKAGES } from "../lib/packages";

export default function Packages() {
  const [page, setPage] = useState(null);
  const [detailPages, setDetailPages] = useState(null); // null = still loading

  useEffect(() => {
    fetchPage("packages").then(setPage).catch(() => {});
    // Detail pages remain the source of card image fallback when the CMS
    // package_cards section hasn't supplied its own image.
    Promise.all(PACKAGES.map(p => fetchPage(`packages-${p.slug}`).catch(() => null)))
      .then(results => {
        const m = {};
        PACKAGES.forEach((p, i) => { m[p.slug] = results[i]; });
        setDetailPages(m);
      })
      .catch(() => setDetailPages({}));
  }, []);

  // Build the card list: prefer the editable `package_cards` CMS section, fall
  // back to the legacy hardcoded PACKAGES array if the admin hasn't populated
  // it yet. Either way each card keeps its slug → routes to /packages/{slug}.
  const cmsCards = page?.sections?.find(s => s.type === "package_cards")?.items || [];
  const cardsLoaded = detailPages !== null;
  const cards = (cmsCards.length > 0 ? cmsCards : PACKAGES).map(c => {
    const fallback = PACKAGES.find(p => p.slug === c.slug) || {};
    const detail = (detailPages || {})[c.slug] || null;
    const detailIntro = detail?.sections?.find(s => s.type === "intro");
    return {
      slug: c.slug,
      image: c.image || detailIntro?.image || fallback.image,
      headline: c.headline || fallback.headline,
      title: c.title || detail?.title || fallback.title,
      blurb: c.blurb || fallback.blurb,
      rate_manual: c.rate_manual || "",
      rate_auto: c.rate_auto || "",
      // Legacy "price" string still falls back so existing /packages renders
      // sensibly until the admin adds explicit manual/auto rates.
      legacyPrice: !c.rate_manual && !c.rate_auto ? (c.price || detailIntro?.title || fallback.price) : "",
    };
  });

  // Strip the package_cards section before passing to PageSections so it
  // doesn't render twice.
  const extraSections = (page?.sections || []).filter(s => s.type !== "package_cards");

  return (
    <>
      <Seo
        title={page?.meta_title || "Choose your driving lesson package — Lime Driving School"}
        description={page?.meta_description}
        jsonLd={page?.schema_json}
      />
      <PageHero
        eyebrow="Our courses"
        title={page?.title || "Choose your driving lesson package"}
        subtitle="Pay as you go or save with a 10- or 20-hour block — every lesson is calm, structured and tailored to you."
        showCta={false}
      />

      <section className="py-12 md:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="packages-grid">
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {cards.map(c => (
            <Link to={`/packages/${c.slug}`} key={c.slug}
                  className="group bg-white border border-black/5 rounded-3xl overflow-hidden flex flex-col hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                  data-testid={`package-card-${c.slug}`}>
              <div className="aspect-[16/9] overflow-hidden bg-[#F3F4F6]">
                {cardsLoaded && c.image && (
                  <img src={c.image} alt={c.title}
                       className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 animate-[fadein_400ms_ease-out]" />
                )}
              </div>
              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#37B160]">{c.headline}</span>
                <h2 className="mt-2 text-2xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{c.title}</h2>
                <p className="mt-3 text-sm text-[#4B5563] leading-relaxed flex-1">{c.blurb}</p>

                {/* Rate footer: manual + auto pair, or legacy single-line price */}
                <div className="mt-5 pt-5 border-t border-black/5">
                  {(c.rate_manual || c.rate_auto) ? (
                    <div className="flex items-end justify-between gap-3">
                      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 flex-1 min-w-0">
                        <dt className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9CA3AF]">Manual</dt>
                        <dt className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9CA3AF]">Automatic</dt>
                        <dd className="text-base font-medium text-[#111827] truncate" style={{fontFamily:"Outfit, sans-serif"}} data-testid={`rate-manual-${c.slug}`}>
                          {c.rate_manual || "—"}
                        </dd>
                        <dd className="text-base font-medium text-[#111827] truncate" style={{fontFamily:"Outfit, sans-serif"}} data-testid={`rate-auto-${c.slug}`}>
                          {c.rate_auto || "—"}
                        </dd>
                      </dl>
                      <span className="text-[#37B160] font-medium text-sm inline-flex items-center gap-1 flex-none group-hover:gap-2 transition-all">
                        View <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{c.legacyPrice}</span>
                      <span className="text-[#37B160] font-medium text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                        View details <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Any extra CMS-driven sections below the cards (icon features, etc.) */}
      <PageSections sections={extraSections} />
    </>
  );
}
